<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	// Svelte
	import { getContext, onDestroy } from 'svelte';
	//Hooks
	import { slide } from 'svelte/transition';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { real_id, get_local_ip } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Services
	import { postReq, localReq } from '../../../services/http';
	//Stores
	import { device_auths } from 'src/stores/device-runtime/local-auth';
	import { getDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	//Design system components
	import { Checkbox, Input, InputV2, Button, Text, Alert } from '@Shelly/shelly-design-system';
	//Components
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	import SettingGuide from '../SettingGuide.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';

	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		errorsLabels = {
			username: 'Username should be at least 1 characters long.',
			password: 'Password should be at least 1 characters long.',
		},
		settings = readDeviceSettings(id);

	let enabled = settings.login.enabled,
		username = settings.login.username,
		password = '',
		errors = { username: false, password: false },
		waitForResponse = false,
		modal: NetworkModal,
		postReqData: {
			id: string;
			enabled: number;
			username?: string;
			password?: string;
		};

	const connect = async () => {
		waitForResponse = true;
		let res: SettingRequest;
		const local_ip = get_local_ip(id);

		if (local_ip) {
			delete postReqData.id;
			res = await localReq(device, `${local_ip}/settings/login`, 'post', postReqData);
		} else {
			res = await postReq(`/device/settings/login/`, postReqData);
		}

		if (res.isok) {
			device_auths.update(device.id, { user: username, pass: password });
			settings.login.enabled = enabled;
			settings.login.username = username;
			devices_settings.update_devices_settings(device.id, settings);
			prepareDeviceData();
		} else {
			onSettingsResponse(res, device);
			waitForResponse = false;
		}
	};

	const prepareDeviceData = () => {
		if (!device?.login) {
			device.login = {};
		}
		device.login.enabled = !enabled;
		device.login.username = window.btoa(username);
		device.login.password = window.btoa(password);
		updateDeviceData();
	};

	const updateDeviceData = async () => {
		const res = await postReq(`/interface/device/save`, {
			id,
			data: JSON.stringify(device),
		});
		if (res.isok) {
			devices.update(device);
		}
		onSettingsResponse(res, device);
		waitForResponse = false;
	};

	const validate = () => {
		postReqData = {
			id,
			enabled: Number(enabled),
		};

		if (enabled) {
			errors.username = username.length == 0;
			errors.password = password.length == 0;
			if (username.length == 0 || password.length == 0) return;
			postReqData = { ...postReqData, username, password };
		}

		modal.open();
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: validate, loading: waitForResponse, disabled: isOffline });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});

	$: InputC = $showNewLayout ? InputV2 : Input;
</script>

<SettingGuide
	text={$format('settings_guide_restrict_login', {
		default:
			'Require a username and password before anyone can change this device from its local web interface.',
	})}
/>

<Checkbox
	size="l"
	class="mb-s"
	bind:checked={enabled}
	disabled={isOffline}
	id="checkbox-l999det"
>
	{capFirstChar(
		$showNewLayout
			? $format('sub_settings_title_restrict_login', { default: 'restrict login' })
			: $format('option_enable', { default: 'enable' }),
	)}
</Checkbox>

{#if !$showNewLayout}
	<Alert
		class="mb-s"
		heading={$format('restrict_description', {
			default: "Restrict the web interface of the device with 'Username' and 'Password'.",
		})}
	/>
{/if}

<hr class="mb-m" />

<div
	class={classMap({
		'cursor-not-allowed': !enabled,
	})}
>
	<svelte:component
		this={InputC}
		aria-label={$format('input_username', { default: 'Input Username' })}
		size="m"
		class="mb-s"
		bind:value={username}
		disabled={!enabled || isOffline}
		error={errors.username ? errorsLabels.username : ''}
		label={capFirstChar($format('placeholder_username', { default: 'username' }))}
		on:input={() => (errors.username = false)}
		id="input-ack09p3"
	/>
	<svelte:component
		this={InputC}
		aria-labelledby="input-nynwo6s"
		a11yProps={{
			iconButtonAriaLabel: capFirstChar($format('label_password', { default: 'password' })),
		}}
		size="m"
		class={classMap({
			'mb-s': !$showNewLayout,
		})}
		label={capFirstChar($format('label_password', { default: 'Password' }))}
		bind:value={password}
		disabled={!enabled || isOffline}
		error={errors.password ? errorsLabels.password : ''}
		type="password"
		icon="fas fa-lock-keyhole"
		on:input={() => (errors.password = false)}
		on:keydown={(e) => {
			if (e.detail.keyCode == 13) {
				validate();
			}
		}}
		id="input-nynwo6s"
	/>
</div>

{#if !$showNewLayout}
	<Button size="m" width="full" on:click={validate} loading={waitForResponse} disabled={isOffline}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}

<NetworkModal
	title={enabled
		? $format('enable_restrict_login_title', {
				default: 'Enable Restrict Login?',
			})
		: $format('disable_restrict_login_title', {
				default: 'Disable Restrict Login?',
			})}
	bind:this={modal}
	on:confirm={connect}
	{isOffline}
	btnTexts={{
		cancelText: '',
		saveText: enabled ? $format('label_enable') : $format('button_disable'),
	}}
>
	{#if enabled}
		{$format('restrict_login_info_text', {
			default:
				"By restricting local login, the web interface of the device will be secured with 'Username' and 'Password' for enhanced security.",
		})}
	{:else}
		{$format('disable_restrict_login_info_text', {
			default:
				"By disabling local login, the web interface of the device will no longer be secured with a 'Username' and 'Password'.",
		})}
	{/if}
</NetworkModal>
